import { useCallback, useEffect, useMemo } from "react";

import { useNavigationContext } from "../api/tenant.hooks";
import type { ContactRole } from "../store";
import { useOnboardingStore, useUserStore } from "../store";
import {
  canAccessPage,
  canProceed,
  getAccessiblePages,
  getNextPage,
  getPreviousPage,
  getStartPage,
  getWaitingMessage
} from "../utils/roleBasedNavigation";

/**
 * Hook for role-based navigation in the onboarding flow.
 *
 * Uses backend-computed navigation context when available, falling back
 * to local computation if the API call fails (rolling deploy compatibility).
 */
export function useRoleBasedNavigation() {
  const uuid = useOnboardingStore((s) => s.uuid);
  const setNavigationContext = useOnboardingStore(
    (s) => s.setNavigationContext
  );
  const navigationContext = useOnboardingStore((s) => s.navigationContext);

  // Backend call
  const {
    data: navContextResponse,
    isLoading,
    isError
  } = useNavigationContext(uuid);

  // Sync backend response into Zustand store
  useEffect(() => {
    if (navContextResponse?.success && navContextResponse.data) {
      setNavigationContext(navContextResponse.data);
      // Also sync stage completion into existing store fields for backwards compat
      const store = useOnboardingStore.getState();
      if (navContextResponse.data.stageCompletion) {
        store.loadStageCompletionFromRequest({
          workdayCompletion: navContextResponse.data.stageCompletion.workday,
          domainCompletion: navContextResponse.data.stageCompletion.domain,
          ssoCompletion: navContextResponse.data.stageCompletion.sso
        });
      }
    }
  }, [navContextResponse, setNavigationContext]);

  // Determine if we should use backend data or fallback to local computation
  const useBackend = !!navigationContext && !isError;

  // --- Backend-driven path ---
  const backendAccessiblePages = useMemo(
    () => navigationContext?.accessiblePages || [],
    [navigationContext]
  );

  const backendUserRoles = useMemo(
    () => (navigationContext?.userRoles || []) as ContactRole[],
    [navigationContext]
  );

  // --- Local fallback path (existing logic) ---
  const localRoles = useUserStore((s) => s.roles);
  const localRolesLoaded = useUserStore((s) => s.rolesLoaded);
  const localStageCompletion = useOnboardingStore((s) => s.stageCompletion);

  const localAccessiblePages = useMemo(
    () => getAccessiblePages(localRoles),
    [localRoles]
  );

  const localStageCompletionBools = useMemo(
    () => ({
      workday: !!localStageCompletion.workday?.completedAt,
      domain: !!localStageCompletion.domain?.completedAt,
      sso: !!localStageCompletion.sso?.completedAt
    }),
    [localStageCompletion]
  );

  // --- Unified interface ---
  const roles = useBackend ? backendUserRoles : localRoles;
  const rolesLoaded = useBackend ? !isLoading : localRolesLoaded;
  const accessiblePages = useBackend
    ? backendAccessiblePages
    : localAccessiblePages;

  const canAccess = useCallback(
    (pagePath: string) => {
      if (useBackend) {
        return backendAccessiblePages.some((p) => p.path === pagePath);
      }
      return canAccessPage(localRoles, pagePath);
    },
    [useBackend, backendAccessiblePages, localRoles]
  );

  const getNext = useCallback(
    (currentPath: string) => {
      if (useBackend) {
        const idx = backendAccessiblePages.findIndex(
          (p) => p.path === currentPath
        );
        return idx >= 0 && idx < backendAccessiblePages.length - 1
          ? backendAccessiblePages[idx + 1].path
          : currentPath;
      }
      return getNextPage(currentPath, useUserStore.getState().roles);
    },
    [useBackend, backendAccessiblePages]
  );

  const getPrevious = useCallback(
    (currentPath: string) => {
      if (useBackend) {
        const idx = backendAccessiblePages.findIndex(
          (p) => p.path === currentPath
        );
        return idx > 0 ? backendAccessiblePages[idx - 1].path : currentPath;
      }
      return getPreviousPage(currentPath, useUserStore.getState().roles);
    },
    [useBackend, backendAccessiblePages]
  );

  const getStartingPage = useCallback(() => {
    if (useBackend) {
      return navigationContext?.startingPage || "/onboarding/completion";
    }
    const currentRoles = useUserStore.getState().roles;
    const sc = useOnboardingStore.getState().stageCompletion;
    return getStartPage(currentRoles, {
      workday: !!sc.workday?.completedAt,
      domain: !!sc.domain?.completedAt,
      sso: !!sc.sso?.completedAt
    });
  }, [useBackend, navigationContext]);

  const canProceedFromStage = useCallback(
    (stage: "workday" | "domain" | "sso") => {
      if (useBackend) {
        return navigationContext?.canProceedFrom?.[stage] ?? false;
      }
      return canProceed(stage, localRoles, localStageCompletionBools);
    },
    [useBackend, navigationContext, localRoles, localStageCompletionBools]
  );

  const waitingMessage = useMemo(() => {
    if (useBackend) {
      return navigationContext?.waitingMessage || null;
    }
    return getWaitingMessage(localRoles, localStageCompletionBools);
  }, [useBackend, navigationContext, localRoles, localStageCompletionBools]);

  const stageCompletion = useBackend
    ? navContextResponse?.data?.stageCompletion || {
        workday: null,
        domain: null,
        sso: null
      }
    : localStageCompletion;

  // isStageComplete must handle both shapes:
  // - Backend: stageCompletion.workday is { completedAt, completedBy } | null
  // - Local fallback: stageCompletion.workday is { completedAt: string | null, completedBy: string | null }
  const isStageComplete = useCallback(
    (stage: "workday" | "domain" | "sso") => {
      const entry = stageCompletion?.[stage];
      if (!entry) return false;
      // For local fallback, object always exists but completedAt may be null
      return "completedAt" in entry ? !!entry.completedAt : !!entry;
    },
    [stageCompletion]
  );

  return {
    roles,
    rolesLoaded,
    accessiblePages,
    canAccess,
    getNext,
    getPrevious,
    getStartingPage,
    canProceedFromStage,
    waitingMessage,
    hasRole: (role: ContactRole) => roles.includes(role),
    isWorkdayAdmin: roles.includes("workday-admin" as ContactRole),
    isItAdmin: roles.includes("it-admin" as ContactRole),
    isSsoAdmin: roles.includes("sso-admin" as ContactRole),
    isStageComplete,
    stageCompletion,
    allPages: accessiblePages,
    // New fields from backend (fallback computes locally for rolling-deploy resilience)
    allStagesComplete: useBackend
      ? (navigationContext?.allStagesComplete ?? false)
      : localStageCompletionBools.workday &&
        localStageCompletionBools.domain &&
        localStageCompletionBools.sso,
    roosterUrl: useBackend ? (navigationContext?.roosterUrl ?? null) : null
  };
}
